import React, { FC } from 'react';
import { Space, Tooltip, Skeleton, Image, Button, Empty, Col, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FileSaver from 'file-saver';
import { DownloadOutlined } from '@ant-design/icons';

import { AmountField, Drawer, TextField } from '~/components';
import { usePageShow } from '~/hooks/usePageQuery';
import { paymentDetails } from '~/routes';
import { PaymentDetails, Terminal } from '~/types';
import PaymentDetailsEdit from './PaymentDetailsEdit';
import { DotIcon } from '~/img';
import { useApi } from '~/hooks';
import { getPaymentDetailsQR } from '~/api/paymentDetails';
import PaymentDetailsDelete from './PaymentDetailsDelete';

interface Props {
  terminalsById: Record<string, Terminal>;
  className?: string;
}

const PaymentDetailsShow: FC<Props> = (props) => {
  const { className, terminalsById } = props;
  const { data, open, showClose } = usePageShow<PaymentDetails>(paymentDetails);

  const terminal = data && (terminalsById[data?.terminalId] as Terminal | undefined);

  const [{ data: QRdata, loading: QRloading }] = useApi(getPaymentDetailsQR(data?.id as string), [
    data?.id,
  ]);

  const downloadQR = () => {
    if (QRdata) {
      FileSaver.saveAs(QRdata, `${data?.name || 'qr'}.png`);
    }
  };

  const [t] = useTranslation('paymentDetails');
  const isTerminalActive = terminal?.status === 'ACTIVE';

  return (
    <Drawer
      className={className}
      title={data?.name}
      onClose={showClose}
      visible={open}
      footer={
        <Row gutter={[8, 8]} justify="end" style={footerStyle}>
          <Col>
            <PaymentDetailsDelete id={data?.id as string} closeParent={showClose} />
          </Col>
          <Col>
            <Button onClick={downloadQR} icon={<DownloadOutlined />} disabled={!isTerminalActive}>
              {t('downloadQR')}
            </Button>
          </Col>
          <Col>
            <PaymentDetailsEdit initialValues={data || {}} />
          </Col>
        </Row>
      }
    >
      <Space direction="vertical" size="large" style={fullWidth}>
        <Space direction="vertical" size="middle" style={fullWidth}>
          <TextField label={t('terminal')}>
            <div className="PaymentDetailsShow-TerminalField">
              {terminal?.label}
              <Tooltip title={terminal?.status && t(`status:${terminal.status}`)}>
                <DotIcon
                  className={`PaymentDetailsShow-StatusDot status-color__${terminal?.status}`}
                />
              </Tooltip>
            </div>
          </TextField>
          <TextField label={t('ID')}>{data?.id}</TextField>
          <TextField label={t('amount')}>
            <AmountField amount={data?.amount} />
          </TextField>
          <TextField label={t('comment')}>{data?.comment}</TextField>
          {isTerminalActive ? (
            <TextField label={t('QR')}>
              <div className="PaymentDetailsShow-QR">
                {QRloading && <Skeleton.Avatar shape="square" size={212} />}
                {QRdata && (
                  <Image
                    src={URL.createObjectURL(QRdata)}
                    alt="QR"
                    width="212px"
                    height="212px"
                    className="PaymentDetailsShow-QRImage"
                  />
                )}
                {!QRloading && !QRdata && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
              </div>
            </TextField>
          ) : (
            <div>
              <TextField label={t('QR')}>{`\u0020`}</TextField>
              <Space className="DeviceCard" direction="vertical">
                <Typography.Text>{t('linkTerminal')}</Typography.Text>
              </Space>
            </div>
          )}
        </Space>
      </Space>
    </Drawer>
  );
};

const fullWidth = { width: '100%' };
const footerStyle = { marginBottom: -4 };

export default styled(PaymentDetailsShow)`
  .PaymentDetailsShow-Title {
    margin-bottom: 0;
  }
  .PaymentDetailsShow-TerminalField {
    display: flex;
    align-items: center;
    .PaymentDetailsShow-StatusDot {
      font-size: 10px;
      margin-left: 4px;
    }
  }
  .PaymentDetailsShow-QR {
    display: flex;
    width: 100%;
    justify-content: center;
    .PaymentDetailsShow-QRImage {
      filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
    }
  }
  .PaymentDetailsShow-BottomTooltip {
    margin-bottom: -8px;
  }
`;
